import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTransitionOrder } from "@/hooks/use-orders";

const CONFIRM_PHRASE = "ENTREGAR";

type ManualCompleteDeliveryDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  orderDailyNumber: number;
  riderName: string | null;
  onCompleted?: () => void;
};

export function ManualCompleteDeliveryDialog({
  open,
  onOpenChange,
  orderId,
  orderDailyNumber,
  riderName,
  onCompleted,
}: ManualCompleteDeliveryDialogProps) {
  const { t } = useTranslation();
  const deliver = useTransitionOrder("deliver");
  const [confirmation, setConfirmation] = useState("");

  useEffect(() => {
    if (!open) {
      setConfirmation("");
    }
  }, [open]);

  const canSubmit = confirmation.trim() === CONFIRM_PHRASE;

  const handleConfirm = async () => {
    if (!canSubmit) {
      return;
    }
    try {
      await deliver.mutateAsync({ orderId });
      toast.success(
        t("orders.delivery.manualCompleteSuccess", {
          defaultValue: "Pedido #{{number}} marcado como entregado",
          number: orderDailyNumber,
        }),
      );
      onCompleted?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Action failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("orders.delivery.manualCompleteTitle", {
              defaultValue: "Marcar delivery como entregado",
            })}
          </DialogTitle>
          <DialogDescription>
            {t("orders.delivery.manualCompleteDescription", {
              defaultValue:
                "Esta acción cierra manualmente el pedido #{{number}} como entregado. El rider será omitido del flujo normal.",
              number: orderDailyNumber,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {riderName ? (
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              <span className="text-muted-foreground">
                {t("orders.delivery.assignedRider", { defaultValue: "Rider" })}:{" "}
              </span>
              <span className="font-medium">{riderName}</span>
            </div>
          ) : null}

          <div className="space-y-1">
            <Label htmlFor="manual-complete-confirm">
              {t("orders.delivery.manualCompleteConfirmLabel", {
                defaultValue: 'Escribí "ENTREGAR" para confirmar',
              })}
            </Label>
            <Input
              autoComplete="off"
              data-testid="manual-complete-confirm-input"
              id="manual-complete-confirm"
              onChange={(event) => setConfirmation(event.target.value)}
              value={confirmation}
            />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} type="button" variant="outline">
            {t("common.cancel", { defaultValue: "Cancelar" })}
          </Button>
          <Button
            data-testid="manual-complete-confirm-button"
            disabled={!canSubmit || deliver.isPending}
            onClick={handleConfirm}
            type="button"
          >
            {t("orders.delivery.manualCompleteConfirm", {
              defaultValue: "Marcar entregado",
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
